import React, { useState, useEffect } from 'react';
import APSLogo from '../../components/ui/APS Logo.svg';
import {
  Shield, CheckCircle2, Eye, EyeOff,
  Mail, Lock, User, Building2, Users,
  ArrowRight, Phone, MailCheck, Briefcase,
} from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PasswordStrengthIndicator, { isPasswordStrong } from '../../components/ui/PasswordStrengthIndicator';
import LoginForm from './components/LoginForm';
import { useAuth } from './hooks/useAuth';
import Modal from '../../components/ui/Modal';
import Dropdown from '../../components/ui/Dropdown';
// createSubscription removed — Individual plan now uses createTrialSubscription inside TrialPaymentStep
import { adminService } from '../admin/services/adminService';
import { getPublicPlans } from '../landing/services/landingService';

// ── Plan config ───────────────────────────────────────────────────────────────
const PLANS = {
  individual: {
    label: 'Individual',
    Icon: User,
    badge: { text: 'Individual Plan', bg: 'bg-blue-500/20', color: 'text-blue-300' },
    panelBorder: '',
    checkColor: 'text-blue-400',
    btnLabel: 'Start 30-Day Trial',
    btnClass: 'bg-primary hover:bg-primary-dark',
    tabActiveColor: 'text-primary',
    tabActiveBorder: 'border-primary',
    tabActiveIcon: 'text-primary',
    heading: 'Start protecting privacy today',
    description:
      '$9.99/mo — Get instant AI-powered analysis of any privacy policy. Detect compliance gaps before regulators do.',
    features: [
      '3 assets per month',
      'GDPR & CCPA compliance checks',
      '30-day free trial included',
      'Instant policy risk scoring',
    ],
  },
  professional: {
    label: 'Professional',
    Icon: Briefcase,
    badge: { text: 'Professional Plan', bg: 'bg-indigo-500/20', color: 'text-indigo-300' },
    panelBorder: '',
    checkColor: 'text-indigo-400',
    btnLabel: 'Get Started',
    btnClass: 'bg-indigo-500 hover:bg-indigo-600',
    tabActiveColor: 'text-indigo-500',
    tabActiveBorder: 'border-indigo-500',
    tabActiveIcon: 'text-indigo-500',
    heading: 'Level up your compliance game',
    description:
      '$49.99/mo — Advanced privacy analysis tools for professionals. Get deeper insights, priority support, and more.',
    features: [
      '10 reports per month',
      'Advanced risk scoring',
      'Priority email support',
      'Detailed compliance reports',
      'Policy comparison tools',
    ],
  },
  business: {
    label: 'Business',
    Icon: Building2,
    badge: { text: 'Business Plan', bg: 'bg-teal-500/20', color: 'text-teal-300' },
    panelBorder: '',
    checkColor: 'text-teal-400',
    btnLabel: 'Start Business Trial',
    btnClass: 'bg-teal-500 hover:bg-teal-600',
    tabActiveColor: 'text-teal-500',
    tabActiveBorder: 'border-teal-500',
    tabActiveIcon: 'text-teal-500',
    heading: 'Grow with confidence & compliance',
    description:
      "$99/mo — Manage all your business's privacy policies in one place. Stay compliant as you scale.",
    features: [
      '50 assets per month',
      'Multi-policy dashboard',
      'Policy change notifications',
      'Team collaboration (up to 10 seats)',
      'Custom branded reports',
    ],
  },
  enterprise: {
    label: 'Enterprise',
    Icon: Users,
    badge: { text: 'Enterprise Plan', bg: 'bg-violet-500/20', color: 'text-violet-300' },
    panelBorder: '',
    checkColor: 'text-violet-400',
    btnLabel: 'Request Enterprise Access',
    btnClass: 'bg-violet-500 hover:bg-violet-600',
    tabActiveColor: 'text-violet-500',
    tabActiveBorder: 'border-violet-500',
    tabActiveIcon: 'text-violet-500',
    heading: 'Enterprise-grade compliance at scale',
    description:
      'Full-spectrum policy governance for large teams. Custom regulations, API access, and dedicated support.',
    features: [
      'Unlimited policy analysis',
      'Custom regulation frameworks',
      'Dedicated account manager',
      'SSO / SAML integration',
      'White-label reports & API access',
      'Priority SLA support',
    ],
  },
};

const PLAN_KEYS = ['individual', 'professional', 'business', 'enterprise'];

const TEAM_SIZE_OPTIONS = [
  '1 – 5', '6 – 10', '11 – 25', '26 – 50', '51 – 100',
];

const EMPLOYEE_OPTIONS = [
  '1 – 50', '51 – 200', '201 – 500', '501 – 1,000', '1,000+',
];

const COUNTRY_CODES = [
  { code: '+1',  flag: '🇺🇸' },
  { code: '+44', flag: '🇬🇧' },
  { code: '+91', flag: '🇮🇳' },
  { code: '+61', flag: '🇦🇺' },
  { code: '+49', flag: '🇩🇪' },
  { code: '+33', flag: '🇫🇷' },
];

// ── Shared field helpers ──────────────────────────────────────────────────────
const inputClass =
  'w-full pl-10 pr-4 py-2.5 border border-surface-border rounded-lg text-sm ' +
  'text-brand-text placeholder-brand-light bg-white focus:outline-none ' +
  'focus:ring-2 focus:ring-primary/30 focus:border-primary transition';

const FieldLabel = ({ children }) => (
  <label className="block text-sm font-medium text-brand-text mb-1.5">{children}</label>
);

const IconWrap = ({ children }) => (
  <span className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none text-brand-light">
    {children}
  </span>
);

// ── Register forms per plan ───────────────────────────────────────────────────
const IndividualForm = ({ form, onChange, showPw, onTogglePw }) => (
  <>
    {/* Full Name */}
    <div>
      <FieldLabel>Full Name</FieldLabel>
      <div className="relative">
        <IconWrap><User className="w-4 h-4" /></IconWrap>
        <input
          type="text" name="fullName" value={form.fullName} onChange={onChange}
          placeholder="John Doe" required className={inputClass}
        />
      </div>
    </div>

    {/* Email */}
    <div>
      <FieldLabel>Email Address</FieldLabel>
      <div className="relative">
        <IconWrap><Mail className="w-4 h-4" /></IconWrap>
        <input
          type="email" name="email" value={form.email} onChange={onChange}
          placeholder="you@email.com" required className={inputClass}
        />
      </div>
    </div>

    {/* Password */}
    <PasswordField showPw={showPw} onTogglePw={onTogglePw} value={form.password} onChange={onChange} />
  </>
);

const ProfessionalForm = ({ form, onChange, showPw, onTogglePw }) => (
  <>
    {/* Full Name */}
    <div>
      <FieldLabel>Full Name</FieldLabel>
      <div className="relative">
        <IconWrap><User className="w-4 h-4" /></IconWrap>
        <input
          type="text" name="fullName" value={form.fullName} onChange={onChange}
          placeholder="John Doe" required className={inputClass}
        />
      </div>
    </div>

    {/* Job Title */}
    <div>
      <FieldLabel>Job Title</FieldLabel>
      <div className="relative">
        <IconWrap><Briefcase className="w-4 h-4" /></IconWrap>
        <input
          type="text" name="jobTitle" value={form.jobTitle} onChange={onChange}
          placeholder="Privacy Officer" required className={inputClass}
        />
      </div>
    </div>

    {/* Email */}
    <div>
      <FieldLabel>Email Address</FieldLabel>
      <div className="relative">
        <IconWrap><Mail className="w-4 h-4" /></IconWrap>
        <input
          type="email" name="email" value={form.email} onChange={onChange}
          placeholder="you@email.com" required className={inputClass}
        />
      </div>
    </div>

    {/* Password */}
    <PasswordField showPw={showPw} onTogglePw={onTogglePw} value={form.password} onChange={onChange} />
  </>
);

const SmbForm = ({ form, onChange, showPw, onTogglePw }) => (
  <>
    {/* Contact Name */}
    <div>
      <FieldLabel>Contact Name</FieldLabel>
      <div className="relative">
        <IconWrap><User className="w-4 h-4" /></IconWrap>
        <input
          type="text" name="contactName" value={form.contactName} onChange={onChange}
          placeholder="Jane Smith" required className={inputClass}
        />
      </div>
    </div>

    {/* Company Name */}
    <div>
      <FieldLabel>Company Name</FieldLabel>
      <div className="relative">
        <IconWrap><Building2 className="w-4 h-4" /></IconWrap>
        <input
          type="text" name="companyName" value={form.companyName} onChange={onChange}
          placeholder="Acme Inc." required className={inputClass}
        />
      </div>
    </div>

    {/* Email */}
    <div>
      <FieldLabel>Email</FieldLabel>
      <div className="relative">
        <IconWrap><Mail className="w-4 h-4" /></IconWrap>
        <input
          type="email" name="email" value={form.email} onChange={onChange}
          placeholder="you@company.com" required className={inputClass}
        />
      </div>
    </div>

    {/* Team Size */}
    <div>
      <FieldLabel>Team Size</FieldLabel>
      <Dropdown
        open={form._teamSizeOpen}
        onOpenChange={(val) => onChange({ target: { name: '_teamSizeOpen', value: val } })}
      >
        <Dropdown.Trigger
          showArrow
          className="w-full justify-between border border-surface-border rounded-lg px-4 py-2.5 text-sm"
        >
          <span className={`flex items-center gap-2 ${!form.teamSize ? 'text-brand-light' : 'text-brand-text'}`}>
            <Users className="w-4 h-4 text-brand-light" />
            {form.teamSize || 'Select team size'}
          </span>
        </Dropdown.Trigger>
        <Dropdown.Menu align="left" width="w-full">
          <Dropdown.Label>Select team size</Dropdown.Label>
          {TEAM_SIZE_OPTIONS.map((o) => (
            <Dropdown.Item
              key={o}
              icon={form.teamSize === o ? <span className="text-teal-500">✓</span> : <span className="w-4" />}
              onClick={() => onChange({ target: { name: 'teamSize', value: o } })}
            >
              {o}
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      </Dropdown>
    </div>

    {/* Password */}
    <PasswordField showPw={showPw} onTogglePw={onTogglePw} value={form.password} onChange={onChange} />
  </>
);

const EnterpriseForm = ({ form, onChange, showPw, onTogglePw }) => (
  <>
    {/* Contact Name */}
    <div>
      <FieldLabel>Contact Name</FieldLabel>
      <div className="relative">
        <IconWrap><User className="w-4 h-4" /></IconWrap>
        <input
          type="text" name="contactName" value={form.contactName} onChange={onChange}
          placeholder="Jane Smith" required className={inputClass}
        />
      </div>
    </div>

    {/* Company Name */}
    <div>
      <FieldLabel>Company Name</FieldLabel>
      <div className="relative">
        <IconWrap><Building2 className="w-4 h-4" /></IconWrap>
        <input
          type="text" name="companyName" value={form.companyName} onChange={onChange}
          placeholder="Enterprise Corp" required className={inputClass}
        />
      </div>
    </div>

    {/* Email */}
    <div>
      <FieldLabel>Email</FieldLabel>
      <div className="relative">
        <IconWrap><Mail className="w-4 h-4" /></IconWrap>
        <input
          type="email" name="email" value={form.email} onChange={onChange}
          placeholder="you@enterprise.com" required className={inputClass}
        />
      </div>
    </div>

    {/* No. of Employees */}
    <div>
      <FieldLabel>No. of Employees</FieldLabel>
      <Dropdown
        open={form._employeesOpen}
        onOpenChange={(val) => onChange({ target: { name: '_employeesOpen', value: val } })}
      >
        <Dropdown.Trigger
          showArrow
          className="w-full justify-between border border-surface-border rounded-lg px-4 py-2.5 text-sm"
        >
          <span className={`flex items-center gap-2 ${!form.employees ? 'text-brand-light' : 'text-brand-text'}`}>
            <Users className="w-4 h-4 text-brand-light" />
            {form.employees || 'Select range'}
          </span>
        </Dropdown.Trigger>
        <Dropdown.Menu align="left" width="w-full">
          <Dropdown.Label>Select range</Dropdown.Label>
          {EMPLOYEE_OPTIONS.map((o) => (
            <Dropdown.Item
              key={o}
              icon={form.employees === o ? <span className="text-violet-500">✓</span> : <span className="w-4" />}
              onClick={() => onChange({ target: { name: 'employees', value: o } })}
            >
              {o}
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      </Dropdown>
    </div>

    {/* Phone Number */}
    <div>
      <FieldLabel>Phone Number</FieldLabel>
      <div className="flex gap-2">
        <select
          name="countryCode" value={form.countryCode} onChange={onChange}
          className="pl-3 pr-7 py-2.5 border border-surface-border rounded-lg text-sm text-brand-text bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition appearance-none w-24 flex-shrink-0"
        >
          {COUNTRY_CODES.map(({ code, flag }) => (
            <option key={code} value={code}>{flag} {code}</option>
          ))}
        </select>
        <div className="relative flex-1">
          <IconWrap><Phone className="w-4 h-4" /></IconWrap>
          <input
            type="tel" name="phone" value={form.phone} onChange={onChange}
            placeholder="(555) 000-0000"
            className={inputClass}
          />
        </div>
      </div>
    </div>
  </>
);

// ── Shared password field ─────────────────────────────────────────────────────
const PasswordField = ({ value, onChange, showPw, onTogglePw }) => (
  <div>
    <FieldLabel>Password</FieldLabel>
    <div className="relative">
      <IconWrap><Lock className="w-4 h-4" /></IconWrap>
      <input
        type={showPw ? 'text' : 'password'}
        name="password" value={value} onChange={onChange}
        placeholder="Min. 8 characters"
        required
        className="w-full pl-10 pr-10 py-2.5 border border-surface-border rounded-lg text-sm text-brand-text placeholder-brand-light bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition"
      />
      <button
        type="button" onClick={onTogglePw}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-light hover:text-brand-muted transition"
      >
        {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
    <PasswordStrengthIndicator password={value} />
  </div>
);

// ── Left panel ────────────────────────────────────────────────────────────────
const LoginLeftPanel = () => (
  <div className="bg-primary w-[420px] flex-shrink-0 flex flex-col p-8 relative overflow-hidden">
    {/* Decorative circles */}
    <div className="absolute -top-20 -left-20 w-72 h-72 bg-white/5 rounded-full pointer-events-none" />
    <div className="absolute -bottom-16 -right-16 w-64 h-64 bg-white/5 rounded-full pointer-events-none" />

    {/* Logo */}
    <div className="flex items-center gap-2 relative z-10">
      <img src={APSLogo} alt="APS Logo" className="h-12 w-auto object-contain" />
    </div>

    {/* Demo card + tagline */}
    <div className="flex-1 flex flex-col justify-center relative z-10 mt-8">
      <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-5 mb-7">
        <div className="flex items-center gap-2.5 mb-4">
          <div className="w-7 h-7 bg-white/20 rounded-md flex items-center justify-center flex-shrink-0">
            <Shield className="w-4 h-4 text-white" />
          </div>
          <div>
            <p className="text-white text-xs font-semibold leading-tight">Policy Analysis Complete</p>
            <p className="text-white/60 text-xs">Acme Corp Privacy Policy</p>
          </div>
        </div>
        <div className="space-y-2.5">
          {[
            { label: 'Risk Score',              badge: '72/100',  bg: 'bg-warning' },
            { label: 'GDPR + CCPA + DPDP',      badge: '84%',     bg: 'bg-success' },
            { label: 'Missing Clauses',         badge: '3 found', bg: 'bg-danger' },
            { label: 'AI Suggestions',          badge: '5 tips',  bg: 'bg-primary' },
          ].map(({ label, badge, bg }) => (
            <div key={label} className="flex items-center justify-between">
              <span className="text-white/75 text-xs">{label}</span>
              <span className={`${bg} text-white text-xs font-bold px-2.5 py-0.5 rounded-md`}>
                {badge}
              </span>
            </div>
          ))}
        </div>
      </div>

      <h2 className="text-white text-2xl font-bold mb-2">Analyze. Comply. Protect.</h2>
      <p className="text-white/70 text-sm leading-relaxed">
        Join 500+ compliance teams using AI to detect privacy risks in seconds.
      </p>
    </div>

    {/* Trust badges */}
    <div className="space-y-2.5 relative z-10">
      {['GDPR Certified Analysis', 'Enterprise-grade Security', 'SOC 2 Type II Compliant'].map(b => (
        <div key={b} className="flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 text-white/60 flex-shrink-0" />
          <span className="text-white/60 text-xs">{b}</span>
        </div>
      ))}
    </div>
  </div>
);

const RegisterLeftPanel = ({ plan }) => {
  const p = PLANS[plan];
  const { Icon } = p;

  return (
    <div className={`bg-[#0F172A] w-[420px] flex-shrink-0 flex flex-col p-8 ${p.panelBorder}`}>
      {/* Logo */}
      <div className="flex items-center gap-2 mb-12">
        <img src={APSLogo} alt="APS Logo" className="h-12 w-auto object-contain" />
      </div>

      {/* Plan badge */}
      <div className={`inline-flex items-center gap-1.5 ${p.badge.bg} rounded-full px-3 py-1 w-fit mb-5`}>
        <Icon className={`w-3.5 h-3.5 ${p.badge.color}`} />
        <span className={`text-xs font-medium ${p.badge.color}`}>{p.badge.text}</span>
      </div>

      {/* Heading */}
      <h2 className="text-white text-2xl font-bold leading-tight mb-3">{p.heading}</h2>
      <p className="text-white/60 text-sm leading-relaxed mb-7">{p.description}</p>

      {/* Features */}
      <div className="space-y-3">
        {p.features.map(f => (
          <div key={f} className="flex items-start gap-2.5">
            <CheckCircle2 className={`w-4 h-4 ${p.checkColor} flex-shrink-0 mt-0.5`} />
            <span className="text-white/80 text-sm">{f}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Main AuthPage ─────────────────────────────────────────────────────────────
const INITIAL_REG_FORM = {
  fullName: '', contactName: '', companyName: '',
  email: '', password: '', teamSize: '', employees: '',
  countryCode: '+91', phone: '',
  jobTitle: '',
  _teamSizeOpen: false, _employeesOpen: false,
};

const AuthPage = ({ defaultMode = 'login' }) => {
  const { register, state, clearError } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const planParam = PLAN_KEYS.includes(searchParams.get('plan')) ? searchParams.get('plan') : null;
  const [mode, setMode]             = useState(planParam ? 'register' : defaultMode);
  const [plan, setPlan]             = useState(planParam || 'individual');
  const [regForm, setRegForm]       = useState(INITIAL_REG_FORM);
  const [showPw, setShowPw]         = useState(false);
  const [agreed, setAgreed]         = useState(false);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [showEnterpriseModal, setShowEnterpriseModal] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [regError, setRegError]     = useState('');

  // Plans marked unavailable are "Coming Soon": they collect interest instead of
  // creating an account, exactly like Enterprise. The backend enforces the same rule.
  const [comingSoonPlans, setComingSoonPlans] = useState(() => new Set());

  useEffect(() => {
    let cancelled = false;
    getPublicPlans()
      .then((res) => {
        if (cancelled) return;
        const raw = res?.data?.plans || res?.data || [];
        setComingSoonPlans(new Set(
          raw.filter((pl) => pl.is_active === false).map((pl) => String(pl.name || '').toLowerCase())
        ));
      })
      .catch(() => {});   // offline or endpoint down: nothing is marked Coming Soon
    return () => { cancelled = true; };
  }, []);

  // ── Individual plan trial state ────────────────────────────────────────────
  const [trialError, setTrialError] = useState('');
  const [indivPlan,  setIndivPlan] = useState(null);      // Individual plan record from API

  // Fetch available plans once so we have the Individual plan_id for createSubscription
  useEffect(() => {
    adminService.getPlans()
      .then((res) => {
        const raw = Array.isArray(res)              ? res
                  : Array.isArray(res?.data?.plans) ? res.data.plans
                  : Array.isArray(res?.data)        ? res.data
                  : [];
        const found = raw.find((p) => p.name?.toLowerCase() === 'individual');
        if (found) setIndivPlan({ id: found.id, name: found.name, price: Number(found.price) || 0 });
      })
      .catch(() => {}); // silently fail — registration still works, payment handled on subscription page
  }, []);

  const handleRegChange = (e) => {
    clearError();
    setRegError('');
    setRegForm(f => ({ ...f, [e.target.name]: e.target.value }));
  };

  const switchPlan = (key) => {
    clearError();
    setPlan(key);
    setRegForm(INITIAL_REG_FORM);
    setShowPw(false);
    setAgreed(false);
    setTrialError('');
  };

  const isContactOnly = plan === 'enterprise' || comingSoonPlans.has(plan);

  const handleRegSubmit = async (e) => {
    e.preventDefault();
    if (!isContactOnly && !agreed) return;
    if (!isContactOnly && !isPasswordStrong(regForm.password)) {
      setRegError('Please create a stronger password that meets all the requirements below.');
      return;
    }
    setRegError('');
    setTrialError('');
    try {
      await register(regForm, plan);

      // Contact-only: no account and no verification email — confirm we'll be in touch.
      if (isContactOnly) {
        setShowEnterpriseModal(true);
        return;
      }

      // All other plans: email-verify flow
      setRegisteredEmail(regForm.email);
      setShowVerifyModal(true);
    } catch (err) {
      // register errors go to state.error via authSlice
      // subscription setup errors go here
      if (err?.message && !state.error) setTrialError(err.message);
    }
  };

  const p = PLANS[plan];

  const handleVerifyOk = () => {
    setShowVerifyModal(false);
    setRegForm(INITIAL_REG_FORM);
    setMode('login');
    navigate('/auth/login');
  };

  return (
    <div className="min-h-screen flex bg-surface">

      {/* ── Full-page loader overlay (account creation) ── */}
      {state.isLoading && mode === 'register' && (
        <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-white/80 backdrop-blur-sm">
          <div className="flex flex-col items-center gap-4">
            <div className="w-14 h-14 rounded-full border-4 border-primary/20 border-t-primary animate-spin" />
            <p className="text-sm font-semibold text-brand-text">Creating your account…</p>
            <p className="text-xs text-brand-muted">Please wait, this will just take a moment.</p>
          </div>
        </div>
      )}
      {/* ── Left panel ── */}
      {mode === 'login'
        ? <LoginLeftPanel />
        : <RegisterLeftPanel plan={plan} />
      }

      {/* ── Right panel ── */}
      <div className="flex-1 flex items-center justify-center p-8">
        {mode === 'login' ? (
          // ── Login view ────────────────────────────────────────────────────
          <LoginForm onCreateAccount={() => { clearError(); setMode('register'); }} />

        ) : (
          // ── Register view ─────────────────────────────────────────────────
          <div className="w-full max-w-md">
            <h1 className="text-2xl font-bold text-brand-text mb-1">
              {isContactOnly ? `Request ${p.label} Access` : 'Create your account'}
            </h1>
            <p className="text-brand-muted text-sm mb-6">
              {isContactOnly
                ? "Fill in your details and our team will reach out to set you up."
                : 'Start your free trial'}
            </p>

            {/* Plan tabs */}
            <div className="flex bg-gray-100 rounded-xl p-1 mb-6">
              {PLAN_KEYS.map(key => {
                const tp = PLANS[key];
                const isActive = plan === key;
                const locked = comingSoonPlans.has(key);
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => !locked && switchPlan(key)}
                    disabled={locked}
                    title={locked ? `${tp.label} is not available yet` : undefined}
                    className={`flex-1 flex flex-col items-center gap-1 py-2.5 rounded-lg text-xs font-medium transition-all ${
                      locked
                        ? 'text-brand-light/60 cursor-not-allowed'
                        : isActive
                        ? `bg-white shadow-sm ${tp.tabActiveColor}`
                        : 'text-brand-muted hover:text-brand-text'
                    }`}
                  >
                    <tp.Icon className={`w-4 h-4 ${locked ? 'text-brand-light/50' : isActive ? tp.tabActiveIcon : 'text-brand-light'}`} />
                    {tp.label}
                    {locked && <span className="text-[9px] leading-none opacity-70">Coming soon</span>}
                  </button>
                );
              })}
            </div>

            <form onSubmit={handleRegSubmit} className="space-y-4">
              {/* Error banner */}
              {(state.error || regError || trialError) && (
                <div className="bg-red-50 border border-red-200 text-red-600 text-sm rounded-lg px-4 py-3">
                  {state.error || regError || trialError}
                </div>
              )}

              {/* Plan-specific fields */}
              {plan === 'individual' && (
                <IndividualForm
                  form={regForm} onChange={handleRegChange}
                  showPw={showPw} onTogglePw={() => setShowPw(v => !v)}
                />
              )}
              {plan === 'professional' && (
                <ProfessionalForm
                  form={regForm} onChange={handleRegChange}
                  showPw={showPw} onTogglePw={() => setShowPw(v => !v)}
                />
              )}
              {plan === 'business' && !isContactOnly && (
                <SmbForm
                  form={regForm} onChange={handleRegChange}
                  showPw={showPw} onTogglePw={() => setShowPw(v => !v)}
                />
              )}
              {isContactOnly && (
                <EnterpriseForm
                  form={regForm} onChange={handleRegChange}
                  showPw={showPw} onTogglePw={() => setShowPw(v => !v)}
                />
              )}

              {/* Terms */}
              {!isContactOnly && (
              <label className="flex items-start gap-2.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={agreed}
                  onChange={e => setAgreed(e.target.checked)}
                  className="mt-0.5 w-4 h-4 rounded border-surface-border accent-primary cursor-pointer flex-shrink-0"
                />
                <span className="text-sm text-brand-muted leading-snug">
                  I agree to the{' '}
                  <button type="button" className="text-primary hover:underline font-medium">Terms of Service</button>
                  {' '}and{' '}
                  <button type="button" className="text-primary hover:underline font-medium">Privacy Policy</button>
                </span>
              </label>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={state.isLoading || (!isContactOnly && !agreed)}
                className={`w-full flex items-center justify-center gap-2 text-white py-3 rounded-lg font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${p.btnClass}`}
              >
                {state.isLoading
                  ? (isContactOnly ? 'Submitting…' : 'Creating account…')
                  : <><span>{isContactOnly ? 'Register Interest' : p.btnLabel}</span><ArrowRight className="w-4 h-4" /></>
                }
              </button>
            </form>

            {!isContactOnly && (
              <p className="text-center text-sm text-brand-muted mt-5">
                Already have an account?{' '}
                <button
                  onClick={() => { clearError(); setMode('login'); }}
                  className="text-primary font-semibold hover:underline"
                >
                  Sign in
                </button>
              </p>
            )}
          </div>
        )}
      </div>

      {/* ── Enterprise request submitted modal ── */}
      <Modal
        isOpen={showEnterpriseModal}
        onClose={() => {
          setShowEnterpriseModal(false);
          setRegForm(INITIAL_REG_FORM);
          setMode('login');
          navigate('/auth/login');
        }}
        title="Request Submitted"
      >
        <div className="flex flex-col items-center gap-5 pt-2">
          <div className="w-14 h-14 rounded-full bg-violet-500/10 flex items-center justify-center">
            <CheckCircle2 className="w-7 h-7 text-violet-500" />
          </div>
          <div className="text-center space-y-2">
            <p className="text-sm text-brand-muted leading-relaxed">
              Thank you for your interest in our {p.label} plan.
            </p>
            <p className="text-sm text-brand-muted leading-relaxed">
              Our admin team will review your request and contact you shortly to get you set up.
            </p>
          </div>
          <button
            onClick={() => {
              setShowEnterpriseModal(false);
              setRegForm(INITIAL_REG_FORM);
              setMode('login');
              navigate('/auth/login');
            }}
            className="w-full py-2.5 rounded-lg bg-violet-500 text-white font-semibold text-sm hover:bg-violet-600 transition-colors"
          >
            Okay, Got It
          </button>
        </div>
      </Modal>

      {/* ── Email confirmation modal ── */}
      <Modal
        isOpen={showVerifyModal}
        onClose={handleVerifyOk}
        title="Check Your Inbox"
      >
        <div className="flex flex-col items-center gap-5 pt-2">
          <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
            <MailCheck className="w-7 h-7 text-primary" />
          </div>
          <div className="text-center space-y-2">
            <p className="text-sm text-brand-muted leading-relaxed">
              We've sent a confirmation link to{' '}
              <span className="font-semibold text-brand-text">{registeredEmail}</span>.
            </p>
            <p className="text-sm text-brand-muted leading-relaxed">
              Please click the link in that email to verify your account before signing in.
            </p>
          </div>
          <button
            onClick={handleVerifyOk}
            className="w-full py-2.5 rounded-lg bg-primary text-white font-semibold text-sm hover:bg-primary/90 transition-colors"
          >
            Okay, Got It
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default AuthPage;
